import { TxAction, TxStep, useBatchStaticCall, useTxSteps, useHandleBatchTx } from "logic/hooks";
import { APP_TOKENS, CHAINS_PEER } from "logic/utils";
import { useEffect } from "react";

import { Loan } from "../data/types";
import { useLoanDetails } from "../LoanDetails/LoanDetailsProvider";
import { useLoansPage } from "../LoansPageProvider";
import { savePendingBridge } from "../usePendingBridgeToast";

import { resolveRepaymentPlan } from "./resolveRepaymentPlan";
import { useBatchBuyTx } from "./useBatchBuyTx";

import { useBatchRepayTx, usePaymentQuote } from ".";

export function useBatchBridgeAndRepay(loan: Loan, customPaymentAmount?: bigint, _isEnabled = true) {
  const { balances, reservesRequired } = useLoanDetails();
  const { usdaiLocked } = useLoansPage();
  const handleBatchTx = useHandleBatchTx();

  const loanChainId = loan.chainId;
  const pyusdDecimals = APP_TOKENS[loanChainId].pyusd.main.decimals;

  const paymentAmount = usePaymentQuote(loan, customPaymentAmount);

  const { deficit, legs, repayAmount, status, amounts } = resolveRepaymentPlan(
    balances,
    paymentAmount,
    pyusdDecimals,
    usdaiLocked ?? 0n,
    reservesRequired,
  );
  const { bridgeAmount, depositAmount, transferAmount } = amounts;

  const isBalanceInsufficient = status === "INSUFFICIENT_BALANCE";
  const isEnabled = _isEnabled && !isBalanceInsufficient && Boolean(paymentAmount && paymentAmount > 0n);

  const needsBridgeStep = bridgeAmount > 0n;
  // Repay is only viable once the plan needs no bridge. Unlike step-status
  // checks, this needs no re-render/refetch to be correct: right after the
  // bridge tx confirms the balances are still stale, so the plan is READY but
  // still contains the bridge leg — canRepay stays false through that window,
  // through LOADING, and through the mid-flight INSUFFICIENT_BALANCE phase.
  const canRepay = status === "READY" && !needsBridgeStep;

  /**************************************************************************/
  /* Initialize Steps */
  /**************************************************************************/

  const { steps, currentStepIndex, updateStepAtIndex, setSteps, isDone } = useTxSteps(getInitialSteps(needsBridgeStep));
  const inFlightTx = steps.some((s) => s.status !== "idle");
  const isBridgePending = steps.length > 1 && steps[0].status === "confirmed" && !canRepay;
  useEffect(() => {
    if (!inFlightTx) {
      setSteps(getInitialSteps(needsBridgeStep));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsBridgeStep, setSteps]);

  /**************************************************************************/
  /* Prepare Batch Calls */
  /**************************************************************************/
  const {
    calls: bridgeBatchCalls,
    error: bridgeError,
    isEnabled: isBridgeEnabled,
  } = useBatchBuyTx({
    depositAmountUnits: bridgeAmount,
    depositToken: APP_TOKENS[loanChainId].pyusd.peer,
    enabled: isEnabled,
  });

  const { calls: depositCalls, error: depositError } = useBatchBuyTx({
    depositAmountUnits: depositAmount,
    depositToken: APP_TOKENS[loanChainId].pyusd.main,
    enabled: isEnabled,
  });

  const { calls: repayCalls, error: repayError } = useBatchRepayTx(loan, customPaymentAmount, transferAmount);

  const repayBatchCalls = depositAmount > 0n ? [...depositCalls, ...repayCalls] : repayCalls;

  /**************************************************************************/
  /* Batch Calls Simulation */
  /**************************************************************************/
  const repayStepIndex = steps.length - 1;

  const { data: bridgeBatchData, error: bridgeBatchSimError } = useBatchStaticCall(bridgeBatchCalls, {
    chainId: CHAINS_PEER[loanChainId],
    enabled: isBridgeEnabled && isEnabled,
  });

  const { data: repayBatchData, error: repayBatchSimError } = useBatchStaticCall(repayBatchCalls, {
    chainId: loanChainId,
    enabled: !isDone && isEnabled && canRepay && repayStepIndex === currentStepIndex,
  });

  /**************************************************************************/
  /* Handle Batched Calls */
  /**************************************************************************/
  async function handleBridge() {
    await handleBatchTx({
      title: "Transfer",
      calls: bridgeBatchCalls,
      chainId: CHAINS_PEER[loanChainId],
      updateStep: (updates) => updateStepAtIndex(0, updates),
      afterConfirming: (receipt) => {
        if (receipt) {
          savePendingBridge({
            txHash: receipt.transactionHash,
            srcChainId: CHAINS_PEER[loanChainId],
            timestamp: Date.now(),
          });
        }
      },
    });
  }

  async function handleRepay() {
    await handleBatchTx({
      title: "Repay",
      calls: repayBatchCalls,
      chainId: loanChainId,
      updateStep: (updates) => updateStepAtIndex(repayStepIndex, updates),
    });
  }

  /**************************************************************************/
  /* Steps Actions */
  /**************************************************************************/

  const bridgeAction: TxAction = {
    send: bridgeBatchData?.request && handleBridge,
    error: bridgeError || bridgeBatchSimError,
  };
  const repayAction: TxAction = {
    send: repayBatchData?.request && handleRepay,
    error: depositError || repayError || repayBatchSimError,
  };
  const actions: TxAction[] = needsBridgeStep || inFlightTx ? [bridgeAction, repayAction] : [repayAction];

  const action = actions[currentStepIndex];

  useEffect(() => {
    setSteps((prev) => prev.map((s, i) => ({ ...s, error: isDone ? null : actions[i]?.error })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgeBatchSimError, repayBatchSimError, depositError, repayError, isDone]);

  useEffect(() => {
    console.log("[debug useBatchBridgeAndRepay]", {
      steps,
      currentStepIndex,
      isDone,
      isLoadingBalances: status === "LOADING",
      needsBridge: needsBridgeStep,
      isBalanceInsufficient,
      canRepay,
      isBridgeInFlight: isBridgePending,
      bridgeBatchData,
      repayBatchData,
      bridgeBatchSimError,
      repayBatchSimError,
      depositError,
      repayError,
      legs,
      deficit,
      repayAmount,
      actions,
      action,
      calls: {
        bridgeBatchCalls,
        depositCalls,
        repayCalls,
      },
    });
  }, [
    steps,
    currentStepIndex,
    isDone,
    status,
    needsBridgeStep,
    isBalanceInsufficient,
    canRepay,
    isBridgePending,
    bridgeBatchData,
    repayBatchData,
    bridgeBatchSimError,
    repayBatchSimError,
    depositError,
    repayError,
    bridgeBatchCalls,
    depositCalls,
    repayCalls,
    legs,
    deficit,
    repayAmount,
  ]);

  return {
    action,
    steps,
    currentStepIndex,
    isDone,

    isLoadingBalances: status === "LOADING",
    needsBridge: needsBridgeStep,
    isBalanceInsufficient,
    canRepay,

    isBridgeInFlight: isBridgePending,
  };
}

function getInitialSteps(needsBridge: boolean, isPrepayment = false): TxStep[] {
  const bridgeStep: TxStep = {
    title: "Transfer",
    action: "Transfer",
    status: "idle",
  };
  const repayStep: TxStep = {
    title: isPrepayment ? "Prepay" : "Pay",
    action: isPrepayment ? "Prepay" : "Make Payment",
    status: "idle",
  };
  return needsBridge ? [bridgeStep, repayStep] : [repayStep];
}
